import { TextField, Popper, Paper, List, ListItemButton } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchTags } from "../utils/helpers/tagHelper";

const PostInput: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) => {
    const [tags, setTags] = useState<string[]>([]);
    const [filteredTags, setFilteredTags] = useState<string[]>([]);
    const [highlightedIndex, setHighlightedIndex] = useState(0);

    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLUListElement | null>(null);

    useEffect(() => {
        fetchTags().then((res) => {
            const tagStrings = res.data.body.map((t: any) => (typeof t === "string" ? t : t.name));
            setTags(tagStrings.filter(Boolean));
        });
    }, []);

    const updateSuggestions = useCallback(
        (val: string, cursorPos: number) => {
            const textBeforeCursor = val.slice(0, cursorPos);
            const lastWord = textBeforeCursor.split(/\s/).pop() || "";
            if (lastWord.startsWith("#")) {
                const search = lastWord.slice(1).toLowerCase();
                const next = tags.map(String).filter((t) => t.toLowerCase().startsWith(search));
                setFilteredTags(next);
                setHighlightedIndex(0);
            } else {
                setFilteredTags([]);
            }
        },
        [tags]
    );

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const val = e.target.value;
            onChange(val);
            const cursorPos = e.target.selectionStart ?? val.length;
            updateSuggestions(val, cursorPos);
        },
        [onChange, updateSuggestions]
    );

    const replaceCurrentHashtag = useCallback(
        (chosen: string) => {
            const cursorPos = inputRef.current?.selectionStart ?? value.length;
            const before = value.slice(0, cursorPos).replace(/#[^\s]*$/, `#${chosen}`);
            const after = value.slice(cursorPos);
            onChange(before + after);
            setFilteredTags([]);
        },
        [onChange, value]
    );

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (filteredTags.length === 0) return;
            const list = listRef.current;
            const item = document.getElementById(`tag-opt-${highlightedIndex}`);

            if (list && item) {
                const target = item.offsetTop - list.clientHeight + item.clientHeight * 2;
                list.scrollTo({ top: target, behavior: "smooth" });
            }

            if (e.key === "ArrowDown") {
                e.preventDefault();
                setHighlightedIndex((i) => (i + 1) % filteredTags.length);
            } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setHighlightedIndex((i) => (i - 1 + filteredTags.length) % filteredTags.length);
            } else if (e.key === "Tab" || e.key === "Enter") {
                e.preventDefault();
                replaceCurrentHashtag(filteredTags[highlightedIndex]);
            } else if (e.key === "Escape") {
                e.preventDefault();
                setFilteredTags([]);
            }
        },
        [filteredTags, highlightedIndex, replaceCurrentHashtag]
    );

    const renderFilteredTags = useMemo(
        () =>
            filteredTags.map((tag, idx) => (
                <ListItemButton
                    key={tag}
                    id={`tag-opt-${idx}`}
                    selected={idx === highlightedIndex}
                    onMouseEnter={() => setHighlightedIndex(idx)}
                    onMouseDown={(e) => e.preventDefault()} // keep focus in the textfield
                    onClick={() => replaceCurrentHashtag(tag)}
                >
                    #{tag}
                </ListItemButton>
            )),
        [filteredTags, highlightedIndex, replaceCurrentHashtag]
    );

    return (
        <>
            <TextField
                inputRef={inputRef}
                variant="outlined"
                placeholder="What are you cooking up?"
                multiline
                maxRows={4}
                value={value}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                fullWidth
                sx={{
                    "& .MuiOutlinedInput-root": { borderRadius: "12px", paddingRight: "8px", background: "transparent" },
                    "& textarea": { padding: "12px", background: "transparent", position: "relative" },
                }}
                inputProps={{
                    "aria-autocomplete": "list",
                    "aria-expanded": filteredTags.length > 0 ? "true" : "false",
                    "aria-activedescendant": filteredTags.length ? `tag-opt-${highlightedIndex}` : undefined,
                }}
            />

            <Popper open={filteredTags.length > 0} anchorEl={inputRef.current} placement="bottom-start">
                <Paper elevation={3}>
                    <List ref={listRef} dense sx={{ maxHeight: 240, overflowY: "auto", minWidth: 200 }}>
                        {renderFilteredTags}
                    </List>
                </Paper>
            </Popper>
        </>
    );
};

export default PostInput;
